import { useParams, Link } from 'react-router-dom';
import { Users, Calendar, Clock, ArrowRight, CheckCircle, User, Award } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { cohorts } from '../data/cohorts';
import { cohortDetails } from '../data/cohortDetails';

const CohortDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const cohort = cohorts.find(c => c.id === parseInt(id));
  const details = cohortDetails[id];

  if (!cohort || !details) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-4">Cohort Not Found</h1>
          <Link to="/services/cohorts" className="text-[var(--accent-cyan)] hover:underline">Back to Cohorts</Link>
        </div>
      </div>
    );
  }

  const handleApply = () => {
    toast.success(`Application started for ${cohort.title}`, { description: 'Redirecting to application form...' });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="cohort-detail-page">
      <Toaster position="top-right" theme={theme} toastOptions={{ style: { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' } }} />

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link to="/services/cohorts" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Cohorts
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 text-xs font-mono uppercase ${cohort.status === 'Enrolling' || cohort.status === 'upcoming' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'}`}>{cohort.status === 'upcoming' ? 'Enrolling' : cohort.status}</span>
                <span className="px-3 py-1 bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)] text-xs font-mono uppercase">{cohort.level}</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-6">{cohort.title}</h1>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-6">{details.summary}</p>
              <div className="flex flex-wrap gap-2">
                {cohort.tags.map((tag, idx) => (<span key={idx} className="px-3 py-1 bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)]/30 text-sm text-[var(--accent-cyan)] font-mono">{tag}</span>))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Start Date</p><p className="text-[var(--text-main)] font-mono">{cohort.startDate}</p></div></div>
                <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Schedule</p><p className="text-[var(--text-main)] font-mono">{cohort.schedule}</p></div></div>
                <div className="flex items-center gap-3"><Users className="w-5 h-5 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Spots Left</p><p className="text-[var(--text-main)] font-mono">{cohort.spotsLeft}/{cohort.cohortSize}</p></div></div>
              </div>
              {cohort.status !== 'archived' && (
                <div className="border-t border-[var(--border)] pt-6">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Program Investment</p>
                  <p className="font-mono text-4xl font-bold text-[var(--accent-cyan)] mb-2">{cohort.price}</p>
                  <p className="text-xs text-[var(--text-muted)]">Payment plans available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Instructor */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./lead_instructor</p>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-[var(--surface-highlight)] border border-[var(--accent-cyan)] flex items-center justify-center font-mono text-[var(--accent-cyan)] text-2xl font-bold">
              {cohort.instructor.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[var(--text-main)]">{cohort.instructor}</h3>
              <p className="text-[var(--text-muted)]">Lead Instructor • Industry Expert</p>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./curriculum_overview</p>
          <div className="space-y-4">
            {details.curriculum.map((week, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--border)]">
                <div className="w-10 h-10 bg-[var(--accent-cyan)]/20 border border-[var(--accent-cyan)] flex items-center justify-center font-mono text-[var(--accent-cyan)] font-bold">{idx + 1}</div>
                <span className="text-[var(--text-main)]">{week}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./skills_you_will_learn</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.topics.map((topic, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 border border-[var(--border)]">
                <CheckCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-16 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./who_is_this_for</p>
          <ul className="space-y-3">
            {details.suitableFor.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <User className="w-5 h-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./what_you_get</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.benefits.map((benefit, idx) => (
              <div key={idx} className="p-4 border border-[var(--border)]">
                <Award className="w-6 h-6 text-[var(--primary)] mb-3" />
                <p className="text-[var(--text-main)]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-8 md:py-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] mb-4">Ready to Transform Your Career?</h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-8">
            Join the {cohort.title} cohort. Only {cohort.spotsLeft} spots remaining.
          </p>
          <button onClick={handleApply} disabled={cohort.status === 'archived'} className="bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_25px_var(--glow-primary)] disabled:opacity-50" data-testid="apply-btn">
            {cohort.status !== 'archived' ? `Apply Now ${cohort.price}` : 'Coming Soon'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default CohortDetailPage;
